import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { themeKeys } from "@/lib/query";
import type { ThemeSettingsInput } from "@/schemas/theme";

/** The workspace theme overrides — null per slot means built-in default. */
export interface ThemeSettings {
  primaryColor: string | null;
  accentColor: string | null;
  sidebarColor: string | null;
  fontBody: string | null;
  fontDisplay: string | null;
}

export function useThemeSettings() {
  return useQuery({
    queryKey: themeKeys.all,
    // Brand colors change rarely; don't refetch on every focus.
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ThemeSettings> => {
      const res = await fetch("/api/theme");
      if (!res.ok) throw new Error("Failed to fetch theme");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useUpdateThemeSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ThemeSettingsInput) => {
      const res = await fetch("/api/admin/theme", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Failed to save theme");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themeKeys.all });
      toast.success("Theme saved");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Failed to save theme");
    },
  });
}
